import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { WireUser } from '@logistics/contracts';
import { failureOf, expectStatus } from '@/lib/result';
import { api } from '../client';
import { OVERVIEW_KEY } from '../overview/use-overview';
import { useToast } from '../shell/toast';

export const USERS_KEY = ['users'] as const;

/** The allow-list. Admin only: nobody else is even asked for it. */
export function useUsers(enabled: boolean): UseQueryResult<WireUser[]> {
  return useQuery({
    queryKey: USERS_KEY,
    queryFn: async () => expectStatus(await api.users.list(), 200).users,
    enabled,
  });
}

type Patch = Parameters<typeof api.users.update>[0]['body'];

interface Change {
  readonly user: WireUser;
  readonly patch?: Patch;
  readonly remove?: true;
  /** The toast on success. */
  readonly done: string;
}

/**
 * One change to one user — role, access, or removal — ending in a toast. A
 * 409 (the last admin, a duplicate) is shown in the server's own words.
 */
export function useChangeUser(): UseMutationResult<void, Error, Change> {
  const toast = useToast();
  const client = useQueryClient();
  return useMutation<void, Error, Change>({
    mutationFn: async ({ user, patch, remove, done }) => {
      const params = { id: user.id };
      const r =
        remove === true
          ? await api.users.remove({ params, body: {} })
          : await api.users.update({ params, body: patch ?? {} });
      if (r.status !== 200) {
        toast(failureOf(r).detail ?? `Could not change ${user.name} (${r.status})`);
        return;
      }
      toast(done);
      await Promise.all([
        client.invalidateQueries({ queryKey: USERS_KEY }),
        client.invalidateQueries({ queryKey: OVERVIEW_KEY }),
      ]);
    },
  });
}
